import { postAuthenticatedRequest, postRequest } from "../../../utils/api/requests/request";
import { ULTIMATE_AGENT_COMMAND_NAMES } from "./ultimateAgentCommands";
import {
  getUltimateAgentCommandContract,
  getUltimateAgentDefaultMock,
} from "./ultimateAgentCommandContracts";

export const ULTIMATE_AGENT_API_MODE = {
  MOCK: "mock",
  LIVE: "live",
};

export const ULTIMATE_AGENT_API_ERROR_EVENT = "ultimate-agent:api-error";

export const getUltimateAgentApiMode = () => {
  const configured = process.env.REACT_APP_ULTIMATE_AGENT_API_MODE?.toLowerCase();
  if (configured === ULTIMATE_AGENT_API_MODE.MOCK || configured === ULTIMATE_AGENT_API_MODE.LIVE) return configured;
  return process.env.NODE_ENV === "production"
    ? ULTIMATE_AGENT_API_MODE.LIVE
    : ULTIMATE_AGENT_API_MODE.MOCK;
};

export const createUltimateAgentMockEnvelope = (data, message = "Executed successfully") => ({
  ok: true,
  message,
  outData: {
    isSuccessful: true,
    message: null,
    data,
    statusCode: "200",
    mimeType: null,
    totalRows: Array.isArray(data) ? data.length : 0,
    totalPages: 0,
    size: 0,
    count: Array.isArray(data) ? data.length : 0,
    pages: 0,
    hasPrevious: false,
    hasNext: false,
  },
  logs: [],
});

const valueKind = (value) => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

const assertContractShape = (actual, expected, path, issues) => {
  // A null fixture marks a nullable/opaque value. Its concrete server value is not constrained.
  if (expected === null || expected === undefined) return;

  const expectedKind = valueKind(expected);
  const actualKind = valueKind(actual);
  if (actualKind !== expectedKind) {
    issues.push(`${path} must be ${expectedKind}; received ${actualKind}`);
    return;
  }

  if (expectedKind === "array") {
    if (!expected.length || !actual.length) return;
    actual.forEach((item, index) => assertContractShape(item, expected[0], `${path}[${index}]`, issues));
    return;
  }

  if (expectedKind === "object") {
    Object.keys(expected).forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(actual, key)) {
        issues.push(`${path}.${key} is missing`);
        return;
      }
      assertContractShape(actual[key], expected[key], `${path}.${key}`, issues);
    });
  }
};

export const validateUltimateAgentResponseContract = (commandName, data) => {
  const contract = getUltimateAgentCommandContract(commandName);
  if (!contract) throw new Error(`Missing Ultimate Agent contract: ${commandName}`);

  const issues = [];
  assertContractShape(data, contract.mockResponse, "outData.data", issues);
  if (issues.length) {
    const error = new Error(`Invalid ${commandName} response contract: ${issues.join("; ")}`);
    error.commandName = commandName;
    error.contractIssues = issues;
    throw error;
  }
  return data;
};

export const unwrapUltimateAgentResponse = (response, commandName) => {
  const result = response?.outData ?? response;
  const successful = response?.ok !== false && result?.isSuccessful !== false;
  if (!successful) {
    const error = new Error(result?.message || response?.message || `${commandName} failed`);
    error.commandName = commandName;
    error.statusCode = result?.statusCode || response?.statusCode;
    error.response = response;
    throw error;
  }

  return {
    commandName,
    data: Object.prototype.hasOwnProperty.call(result || {}, "data") ? result.data : result,
    message: result?.message || response?.message,
    totalRows: result?.totalRows ?? 0,
    raw: response,
  };
};

const resolveMock = async (mock, payload) => {
  if (typeof mock === "function") return mock(payload);
  return mock;
};

export const executeUltimateAgentCommand = async (
  command,
  payload = {},
  options = {},
) => {
  const commandName = typeof command === "string" ? command : command?.name;
  if (!ULTIMATE_AGENT_COMMAND_NAMES.has(commandName)) {
    throw new Error(`Unknown Ultimate Agent command: ${commandName || "undefined"}`);
  }

  const mode = options.mode || getUltimateAgentApiMode();
  let response;
  if (mode === ULTIMATE_AGENT_API_MODE.MOCK) {
    const configuredMock = options.mock === undefined
      ? getUltimateAgentDefaultMock(commandName)
      : options.mock;
    const mockData = await resolveMock(configuredMock, payload);
    response = mockData?.outData ? mockData : createUltimateAgentMockEnvelope(mockData);
  } else {
    if (options.transport) {
      response = await options.transport(commandName, payload, options.isBackOffice || false, options.headers || null);
    } else if (command?.access === "public") {
      response = await postRequest(commandName, payload, undefined, options.headers || null);
    } else {
      response = await postAuthenticatedRequest(commandName, payload, options.isBackOffice || false, options.headers || null);
    }
  }

  const normalized = unwrapUltimateAgentResponse(response, commandName);
  const validateContract = options.validateContract ?? mode === ULTIMATE_AGENT_API_MODE.LIVE;
  if (validateContract) validateUltimateAgentResponseContract(commandName, normalized.data);

  return { ...normalized, mode };
};
